import React, { useEffect, useState, useRef, Component } from 'react';
import { cn } from '@/lib/utils';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeftIcon, HomeIcon, LockIcon, UnlockIcon, ChevronRightIcon, ChevronDownIcon, CheckIcon, ArrowRightIcon, ShareIcon, DownloadIcon, RefreshCwIcon, CreditCardIcon, ReceiptIcon, SettingsIcon, ShieldIcon, SlidersIcon, EyeIcon, EyeOffIcon, PlusIcon, Trash2Icon, AlertCircleIcon, SmartphoneIcon, GlobeIcon, WifiIcon, MapPinIcon, XIcon } from 'lucide-react';
import { useTheme } from '../utils/theme';
interface CardsScreenProps {
  onBack?: () => void;
  onNavigateHome?: () => void;
}
type Step = 'tabs' | 'summary' | 'pay_bill' | 'review' | 'processing' | 'success' | 'manage_cards' | 'card_detail' | 'card_controls' | 'spending_limits' | 'blocked_merchants' | 'pin_management' | 'pin_enter_old' | 'pin_enter_new' | 'pin_confirm_new' | 'pin_success' | 'activation_entry' | 'activation_details' | 'activation_set_pin' | 'activation_success' | 'request_entry' | 'request_type' | 'request_delivery' | 'request_review' | 'request_processing' | 'request_success' | 'apply_account' | 'apply_scheme' | 'apply_choose_card' | 'apply_name_delivery' | 'apply_review' | 'apply_processing' | 'apply_success';
type TabId = 'credit' | 'debit';
const CAROUSEL_CARDS = [{
  id: 'cc-1',
  type: 'credit',
  label: 'Gold',
  number: '•••• •••• •••• 7231',
  expires: '06/27',
  isNew: false,
  isActive: true
}, {
  id: 'cc-2',
  type: 'credit',
  label: 'Platinum',
  number: '•••• •••• •••• 8892',
  expires: '09/28',
  isNew: true,
  isActive: false,
  inactiveReason: 'Pending Activation'
}, {
  id: 'db-1',
  type: 'debit',
  label: 'Classic',
  number: '•••• •••• •••• 4892',
  expires: '12/28',
  isNew: false,
  isActive: true
}, {
  id: 'db-2',
  type: 'debit',
  label: 'Platinum',
  number: '•••• •••• •••• 5567',
  expires: '03/29',
  isNew: false,
  isActive: false,
  inactiveReason: 'Locked by User'
}];
const CREDIT_CARD = {
  id: 'cc-1',
  type: 'CREDIT' as const,
  tier: 'Gold',
  number: '•••• •••• •••• 7231',
  fullNumber: '4521 1234 5678 7231',
  cardholder: 'KASHISH',
  expires: '06/27',
  totalLimit: 250000,
  availableLimit: 170000,
  minDue: 800,
  totalDue: 80000,
  dueDate: '15 Jun 2026',
  status: 'Active'
};
const DEBIT_CARDS = [{
  id: 'db-1',
  type: 'DEBIT' as const,
  tier: 'Classic',
  number: '•••• •••• •••• 4892',
  cardholder: 'KASHISH',
  expires: '12/28',
  status: 'Active'
}, {
  id: 'db-2',
  type: 'DEBIT' as const,
  tier: 'Platinum',
  number: '•••• •••• •••• 5567',
  cardholder: 'KASHISH',
  expires: '03/29',
  status: 'Locked'
}];
const VIRTUAL_CARDS = [{
  id: 'vc-1',
  type: 'VIRTUAL' as const,
  tier: 'E-Card',
  number: '•••• •••• •••• 9912',
  fullNumber: '4242 4242 4242 9912',
  cvv: '123',
  cardholder: 'KASHISH',
  expires: '01/25',
  status: 'Active'
}];
const PAY_FROM_ACCOUNTS = [{
  id: 'current',
  holder: 'Kashish',
  label: 'Current Account',
  number: '......0123 4567 8910',
  balance: 282992
}, {
  id: 'savings',
  holder: 'Kashish',
  label: 'Savings Account',
  number: '......0456 7890 1234',
  balance: 145200
}];
const TRANSACTIONS = [{
  id: 1,
  merchant: 'Netflix',
  date: 'Today, 10:23 AM',
  amount: 1500,
  type: 'debit'
}, {
  id: 2,
  merchant: 'Foodpanda',
  date: 'Yesterday, 8:45 PM',
  amount: 2350,
  type: 'debit'
}, {
  id: 3,
  merchant: 'Uber',
  date: '12 Jun, 9:15 AM',
  amount: 850,
  type: 'debit'
}, {
  id: 4,
  merchant: 'Payment Received',
  date: '10 Jun, 2:00 PM',
  amount: 50000,
  type: 'credit'
}];
const BLOCKED_MERCHANTS = [{
  id: 1,
  name: 'Daraz.pk',
  date: '15 May 2026'
}, {
  id: 2,
  name: 'AliExpress',
  date: '02 Jun 2026'
}];
const slideVariants = {
  enter: (dir: number) => ({
    x: dir > 0 ? '100%' : '-100%',
    opacity: 0
  }),
  center: {
    x: 0,
    opacity: 1,
    transition: {
      duration: 0.3,
      ease: 'easeOut'
    }
  },
  exit: (dir: number) => ({
    x: dir < 0 ? '100%' : '-100%',
    opacity: 0,
    transition: {
      duration: 0.3,
      ease: 'easeIn'
    }
  })
};
// ===== Visual Components =====
function CreditCardVisual({
  number,
  cardholder,
  expires,
  small = false,
  showFull = false,
  fullNumber = ''
}: any) {
  return <div className={`w-full ${small ? 'aspect-[1.586/1]' : 'aspect-[1.586/1]'} rounded-2xl relative overflow-hidden`} style={{
    background: 'linear-gradient(135deg, #1A0E08 0%, #3D1A0A 45%, #C0542A 100%)',
    boxShadow: '0 12px 32px rgba(58,18,8,0.35)'
  }} data-id="element-306">
      <div aria-hidden className="absolute inset-0 pointer-events-none" style={{
      background: 'radial-gradient(circle at 110% -10%, rgba(243,112,36,0.45) 0%, rgba(243,112,36,0) 55%)'
    }} data-id="element-307" />
      <div aria-hidden className="absolute inset-0 pointer-events-none opacity-[0.08]" style={{
      backgroundImage: 'repeating-linear-gradient(45deg, #fff 0 1px, transparent 1px 18px)'
    }} data-id="element-308" />
      <div className="relative z-10 p-5 h-full flex flex-col justify-between" data-id="element-309">
        <div className="flex justify-between items-start" data-id="element-310">
          <div data-id="element-311">
            <p className="text-white/70 text-[9px] font-bold uppercase tracking-[0.18em] mb-1" data-id="element-312">
              CREDIT
            </p>
            <p className="text-white text-[16px] font-bold" data-id="element-313">Gold</p>
          </div>
          <img src="https://cdn.magicpatterns.com/uploads/k5mzauqmFki77bNt7CFYh1/Whatsapp_digiBOP-removebg-preview.png" alt="" className="h-7 w-auto brightness-0 invert" data-id="element-314" />
        </div>
        <div className="flex items-center gap-2 -mt-1" data-id="element-315">
          <div className="w-9 h-7 rounded-md" style={{
          background: 'linear-gradient(135deg, #F9D07A 0%, #E0A640 50%, #F9D07A 100%)',
          boxShadow: 'inset 0 0 0 1px rgba(0,0,0,0.18)'
        }} data-id="element-316" />
        </div>
        <div data-id="element-317">
          <p className="text-white text-[18px] font-mono tracking-[0.18em] mb-3" data-id="element-318">
            {showFull ? fullNumber : number}
          </p>
          <div className="flex justify-between items-end" data-id="element-319">
            <div data-id="element-320">
              <p className="text-white/60 text-[8px] font-bold uppercase tracking-[0.18em] mb-0.5" data-id="element-321">
                Cardholder
              </p>
              <p className="text-white text-[13px] font-bold uppercase tracking-wider" data-id="element-322">
                {cardholder}
              </p>
            </div>
            <div className="text-right" data-id="element-323">
              <p className="text-white/60 text-[8px] font-bold uppercase tracking-[0.18em] mb-0.5" data-id="element-324">
                Valid Thru
              </p>
              <p className="text-white text-[13px] font-bold" data-id="element-325">{expires}</p>
            </div>
          </div>
        </div>
      </div>
    </div>;
}
function DebitCardVisual({
  number,
  cardholder,
  expires,
  tier,
  showFull = false,
  fullNumber = ''
}: any) {
  const gradient = tier === 'Platinum' ? 'linear-gradient(135deg, #1A1A2E 0%, #16213E 100%)' : 'linear-gradient(135deg, #661806 0%, #3D1A0A 100%)';
  return <div className="w-full aspect-[1.586/1] rounded-2xl relative overflow-hidden" style={{
    background: gradient,
    boxShadow: '0 10px 24px rgba(0,0,0,0.22)'
  }} data-id="element-326">
      <div className="relative z-10 p-5 h-full flex flex-col justify-between" data-id="element-327">
        <div className="flex justify-between items-start" data-id="element-328">
          <div data-id="element-329">
            <p className="text-white/70 text-[9px] font-bold uppercase tracking-[0.18em] mb-1" data-id="element-330">
              DEBIT
            </p>
            <p className="text-white text-[16px] font-bold" data-id="element-331">{tier}</p>
          </div>
          <img src="https://cdn.magicpatterns.com/uploads/k5mzauqmFki77bNt7CFYh1/Whatsapp_digiBOP-removebg-preview.png" alt="" className="h-7 w-auto brightness-0 invert" data-id="element-332" />
        </div>
        <div data-id="element-333">
          <p className="text-white text-[18px] font-mono tracking-[0.18em] mb-3" data-id="element-334">
            {showFull ? fullNumber : number}
          </p>
          <div className="flex justify-between items-end" data-id="element-335">
            <div data-id="element-336">
              <p className="text-white/60 text-[8px] font-bold uppercase tracking-[0.18em] mb-0.5" data-id="element-337">
                Cardholder
              </p>
              <p className="text-white text-[13px] font-bold uppercase tracking-wider" data-id="element-338">
                {cardholder}
              </p>
            </div>
            <div className="text-right" data-id="element-339">
              <p className="text-white/60 text-[8px] font-bold uppercase tracking-[0.18em] mb-0.5" data-id="element-340">
                Valid Thru
              </p>
              <p className="text-white text-[13px] font-bold" data-id="element-341">{expires}</p>
            </div>
          </div>
        </div>
      </div>
    </div>;
}
function VirtualCardVisual({
  number,
  cardholder,
  expires,
  showFull = false,
  fullNumber = '',
  cvv = ''
}: any) {
  return <div className="w-full aspect-[1.586/1] rounded-2xl relative overflow-hidden" style={{
    background: 'linear-gradient(135deg, #0F2027 0%, #203A43 50%, #2C5364 100%)',
    boxShadow: '0 10px 24px rgba(0,0,0,0.22)'
  }} data-id="element-342">
      <div className="relative z-10 p-5 h-full flex flex-col justify-between" data-id="element-343">
        <div className="flex justify-between items-start" data-id="element-344">
          <div data-id="element-345">
            <p className="text-white/70 text-[9px] font-bold uppercase tracking-[0.18em] mb-1" data-id="element-346">
              VIRTUAL
            </p>
            <p className="text-white text-[16px] font-bold" data-id="element-347">E-Card</p>
          </div>
          <WifiIcon className="w-6 h-6 text-white/50" data-id="element-348" />
        </div>
        <div data-id="element-349">
          <p className="text-white text-[18px] font-mono tracking-[0.18em] mb-3" data-id="element-350">
            {showFull ? fullNumber : number}
          </p>
          <div className="flex justify-between items-end" data-id="element-351">
            <div data-id="element-352">
              <p className="text-white/60 text-[8px] font-bold uppercase tracking-[0.18em] mb-0.5" data-id="element-353">
                Cardholder
              </p>
              <p className="text-white text-[13px] font-bold uppercase tracking-wider" data-id="element-354">
                {cardholder}
              </p>
            </div>
            <div className="flex gap-4 text-right" data-id="element-355">
              {showFull && <div data-id="element-356">
                  <p className="text-white/60 text-[8px] font-bold uppercase tracking-[0.18em] mb-0.5" data-id="element-357">
                    CVV
                  </p>
                  <p className="text-white text-[13px] font-bold" data-id="element-358">{cvv}</p>
                </div>}
              <div data-id="element-359">
                <p className="text-white/60 text-[8px] font-bold uppercase tracking-[0.18em] mb-0.5" data-id="element-360">
                  Valid Thru
                </p>
                <p className="text-white text-[13px] font-bold" data-id="element-361">{expires}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>;
}
export function CardsScreen({
  onBack,
  onNavigateHome
}: CardsScreenProps) {
  const {
    isDark
  } = useTheme();
  const [step, setStep] = useState<Step>('tabs');
  const [direction, setDirection] = useState(1);
  const [tab, setTab] = useState<TabId>('credit');
  const [isLocked, setIsLocked] = useState(false);
  const [selectedCard, setSelectedCard] = useState<any>(CREDIT_CARD);
  // Toggles state
  const [toggles, setToggles] = useState({
    ecommerce: true,
    contactless: true,
    international: false,
    atm: true
  });
  const [pendingToggle, setPendingToggle] = useState<string | null>(null);
  // Block card flow state
  const [blockReason, setBlockReason] = useState<string | null>(null);
  // Virtual card state
  const [showVirtualDetails, setShowVirtualDetails] = useState(false);
  // PIN state
  const [pinInput, setPinInput] = useState('');
  // Pay-bill state
  const [payAmount, setPayAmount] = useState<string>('');
  const [payFrom, setPayFrom] = useState(PAY_FROM_ACCOUNTS[0]);
  const [showAccountDropdown, setShowAccountDropdown] = useState(false);
  // Apply Flow State
  const [selectedApplyAccount, setSelectedApplyAccount] = useState<string | null>(null);
  const [selectedScheme, setSelectedScheme] = useState<'paypak' | 'mastercard' | null>(null);
  const [selectedApplyCard, setSelectedApplyCard] = useState<any>(null);
  const [expandedApplyCard, setExpandedApplyCard] = useState<string | null>(null);
  const [nameOnCard, setNameOnCard] = useState('');
  const [applyAddressType, setApplyAddressType] = useState<'home' | 'office'>('home');
  const [applyAddress, setApplyAddress] = useState('');
  const [applyTermsAgreed, setApplyTermsAgreed] = useState(false);
  // Carousel State
  const [cardStatuses, setCardStatuses] = useState<Record<string, boolean>>({
    'cc-1': true,
    'cc-2': false,
    'db-1': true,
    'db-2': false
  });
  const [carouselActiveIndex, setCarouselActiveIndex] = useState(0);
  const carouselRef = useRef<HTMLDivElement>(null);
  const cardRefs = useRef<(HTMLDivElement | null)[]>([]);
  const navigateTo = (next: Step, dir = 1) => {
    setDirection(dir);
    setStep(next);
  };
  const formatPKR = (n: number) => n.toLocaleString('en-US', {
    minimumFractionDigits: 0
  });
  useEffect(() => {
    if (step === 'processing' || step === 'request_processing' || step === 'apply_processing') {
      const t = setTimeout(() => {
        if (step === 'processing') navigateTo('success');else if (step === 'request_processing') navigateTo('request_success');else navigateTo('apply_success');
      }, 1800);
      return () => clearTimeout(t);
    }
  }, [step]);
  // Carousel scroll listener
  useEffect(() => {
    const root = carouselRef.current;
    if (!root || step !== 'tabs') return;
    const updateActive = () => {
      const rootRect = root.getBoundingClientRect();
      const rootCenter = rootRect.left + rootRect.width / 2;
      let closestIdx = 0;
      let closestDist = Infinity;
      cardRefs.current.forEach((el, i) => {
        if (!el) return;
        const rect = el.getBoundingClientRect();
        const center = rect.left + rect.width / 2;
        const dist = Math.abs(center - rootCenter);
        if (dist < closestDist) {
          closestDist = dist;
          closestIdx = i;
        }
      });
      setCarouselActiveIndex(closestIdx);
    };
    root.addEventListener('scroll', updateActive, {
      passive: true
    });
    // Reset scroll when tab changes
    root.scrollTo({
      left: 0,
      behavior: 'instant'
    });
    updateActive();
    return () => root.removeEventListener('scroll', updateActive);
  }, [tab, step]);
  const textPri = isDark ? 'text-white' : 'text-[#303030]';
  const textSec = isDark ? 'text-[#B8A89E]' : 'text-[#818285]';
  const cardSurface = isDark ? 'bg-[#2D1810] border border-[rgba(243,112,36,0.12)]' : 'bg-white border border-[#F0E8E3]';
  // ===== Reusable Components =====
  const Header = ({
    title,
    onBackClick = () => navigateTo('tabs', -1)
  }: {
    title: string;
    onBackClick?: () => void;
  }) => <div className="px-6 pt-6 pb-4 flex items-center justify-between" data-id="element-362">
      <button onClick={onBackClick} aria-label="Back" className={`p-2 -ml-2 ${textPri}`} data-id="element-363">
        <ArrowLeftIcon className="w-6 h-6" strokeWidth={2} data-id="element-364" />
      </button>
      <h1 className={`text-[18px] font-bold ${textPri}`} data-id="element-365">{title}</h1>
      <button onClick={onNavigateHome} aria-label="Home" className={`w-10 h-10 rounded-full border-2 flex items-center justify-center ${isDark ? 'border-[#F37024] text-[#F37024]' : 'border-[#5A1408] text-[#5A1408]'}`} data-id="element-366">
        <HomeIcon className="w-5 h-5" strokeWidth={2} data-id="element-367" />
      </button>
    </div>;
  const PinEntry = ({
    title,
    subtitle,
    onComplete
  }: {
    title: string;
    subtitle: string;
    onComplete: () => void;
  }) => <div className="px-6 flex-1 flex flex-col items-center justify-center" data-id="element-368">
      <h2 className={`text-[24px] font-bold mb-2 ${textPri}`} data-id="element-369">{title}</h2>
      <p className={`text-[14px] mb-8 text-center ${textSec}`} data-id="element-370">{subtitle}</p>
      <div className="flex gap-4 mb-12" data-id="element-371">
        {[0, 1, 2, 3].map(i => <div key={i} className={`w-12 h-14 rounded-xl border-2 flex items-center justify-center text-[24px] font-bold ${pinInput.length > i ? isDark ? 'border-[#F37024] text-white' : 'border-[#5A1408] text-[#303030]' : isDark ? 'border-[#3D2518]' : 'border-[#E5D5C5]'}`} data-id="element-372">
            {pinInput.length > i ? '•' : ''}
          </div>)}
      </div>
      <div className="grid grid-cols-3 gap-x-8 gap-y-6 w-full max-w-[280px]" data-id="element-373">
        {[1, 2, 3, 4, 5, 6, 7, 8, 9].map(num => <button key={num} onClick={() => {
        if (pinInput.length < 4) {
          const newPin = pinInput + num;
          setPinInput(newPin);
          if (newPin.length === 4) setTimeout(onComplete, 300);
        }
      }} className={`text-[24px] font-medium py-2 active:scale-90 transition-transform ${textPri}`} data-id="element-374">
            {num}
          </button>)}
        <div data-id="element-375" />
        <button onClick={() => {
        if (pinInput.length < 4) {
          const newPin = pinInput + '0';
          setPinInput(newPin);
          if (newPin.length === 4) setTimeout(onComplete, 300);
        }
      }} className={`text-[24px] font-medium py-2 active:scale-90 transition-transform ${textPri}`} data-id="element-376">
          0
        </button>
        <button onClick={() => setPinInput(prev => prev.slice(0, -1))} className={`flex items-center justify-center py-2 active:scale-90 transition-transform ${textPri}`} data-id="element-377">
          <ArrowLeftIcon className="w-6 h-6" data-id="element-378" />
        </button>
      </div>
    </div>;
  const SuccessView = ({
    title,
    subtitle,
    actionText,
    onAction
  }: {
    title: string;
    subtitle: string;
    actionText: string;
    onAction: () => void;
  }) => <div className="px-6 flex-1 flex flex-col items-center justify-center text-center" data-id="element-379">
      <motion.div initial={{
      scale: 0
    }} animate={{
      scale: 1
    }} transition={{
      type: 'spring',
      damping: 14,
      stiffness: 200
    }} className="w-20 h-20 rounded-full bg-[#10B981]/15 flex items-center justify-center mb-6" data-id="element-380">
        <div className="w-14 h-14 rounded-full bg-[#10B981] flex items-center justify-center" data-id="element-381">
          <CheckIcon className="w-8 h-8 text-white" strokeWidth={3} data-id="element-382" />
        </div>
      </motion.div>
      <h2 className={`text-[24px] font-bold mb-2 ${textPri}`} data-id="element-383">{title}</h2>
      <p className={`text-[14px] mb-8 ${textSec}`} data-id="element-384">{subtitle}</p>
      <button onClick={onAction} className="w-full py-4 bg-[#5A1408] text-white font-bold text-[16px] rounded-2xl shadow-md active:scale-[0.98] transition-transform" data-id="element-385">
        {actionText}
      </button>
    </div>;
  // ===== Apply flow steps render functions =====
  const renderApplyAccount = () => <motion.div key="apply_account" custom={direction} variants={slideVariants} initial="enter" animate="center" exit="exit" className={`w-full min-h-screen pb-8 flex flex-col ${isDark ? 'bg-[#1A0E08]' : 'bg-page-warm'}`} data-id="element-386">
      <Header title="Select Account" data-id="element-387" />
      <div className="px-6 mt-4 flex-1" data-id="element-388">
        <p className={`text-[14px] mb-6 ${textSec}`} data-id="element-389">
          Select the account you want to link to your new card.
        </p>
        <div className="space-y-3" data-id="element-390">
          {PAY_FROM_ACCOUNTS.map(acc => <button key={acc.id} onClick={() => setSelectedApplyAccount(acc.id)} className={`w-full p-4 rounded-2xl border-2 text-left transition-colors ${selectedApplyAccount === acc.id ? isDark ? 'border-[#F37024] bg-[#F37024]/10' : 'border-[#F37024] bg-[#F37024]/5' : cardSurface}`} data-id="element-391">
              <div className="flex items-center justify-between mb-1" data-id="element-392">
                <span className={`text-[14px] font-bold ${textPri}`} data-id="element-393">
                  {acc.label}
                </span>
                <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${selectedApplyAccount === acc.id ? 'border-[#F37024]' : isDark ? 'border-[#3D2518]' : 'border-[#E5D5C5]'}`} data-id="element-394">
                  {selectedApplyAccount === acc.id && <div className="w-2.5 h-2.5 rounded-full bg-[#F37024]" data-id="element-395" />}
                </div>
              </div>
              <p className={`text-[12px] ${textSec} mb-2`} data-id="element-396">{acc.number}</p>
              <p className={`text-[14px] font-bold ${textPri}`} data-id="element-397">
                Rs. {formatPKR(acc.balance)}
              </p>
            </button>)}
        </div>
      </div>
      <div className="px-6 mt-auto pt-6" data-id="element-398">
        <button onClick={() => navigateTo('apply_scheme')} disabled={!selectedApplyAccount} className="w-full py-4 bg-[#F37024] text-white font-bold text-[16px] rounded-2xl shadow-md active:scale-[0.98] transition-transform disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-399">
          Continue
        </button>
      </div>
    </motion.div>;
  const renderApplyScheme = () => <motion.div key="apply_scheme" custom={direction} variants={slideVariants} initial="enter" animate="center" exit="exit" className={`w-full min-h-screen pb-8 flex flex-col ${isDark ? 'bg-[#1A0E08]' : 'bg-page-warm'}`} data-id="element-400">
      <Header title="Select Scheme" onBackClick={() => navigateTo('apply_account', -1)} data-id="element-401" />
      <div className="px-6 mt-4 flex-1" data-id="element-402">
        <p className={`text-[14px] mb-6 ${textSec}`} data-id="element-403">
          Choose the payment network for your new card.
        </p>
        <div className="space-y-3" data-id="element-404">
          {[{
          id: 'mastercard',
          label: 'Mastercard',
          desc: 'Global acceptance, premium benefits'
        }, {
          id: 'paypak',
          label: 'PayPak',
          desc: 'Domestic acceptance, lower fees'
        }].map(scheme => <button key={scheme.id} onClick={() => setSelectedScheme(scheme.id as any)} className={`w-full p-4 rounded-2xl border-2 text-left transition-colors ${selectedScheme === scheme.id ? isDark ? 'border-[#F37024] bg-[#F37024]/10' : 'border-[#F37024] bg-[#F37024]/5' : cardSurface}`} data-id="element-405">
              <div className="flex items-center justify-between mb-1" data-id="element-406">
                <span className={`text-[16px] font-bold ${textPri}`} data-id="element-407">
                  {scheme.label}
                </span>
                <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${selectedScheme === scheme.id ? 'border-[#F37024]' : isDark ? 'border-[#3D2518]' : 'border-[#E5D5C5]'}`} data-id="element-408">
                  {selectedScheme === scheme.id && <div className="w-2.5 h-2.5 rounded-full bg-[#F37024]" data-id="element-409" />}
                </div>
              </div>
              <p className={`text-[13px] ${textSec}`} data-id="element-410">{scheme.desc}</p>
            </button>)}
        </div>
      </div>
      <div className="px-6 mt-auto pt-6" data-id="element-411">
        <button onClick={() => navigateTo('apply_choose_card')} disabled={!selectedScheme} className="w-full py-4 bg-[#F37024] text-white font-bold text-[16px] rounded-2xl shadow-md active:scale-[0.98] transition-transform disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-412">
          Continue
        </button>
      </div>
    </motion.div>;
  const renderApplyChooseCard = () => {
    const cards = [{
      id: 'gold',
      name: 'BOP Gold Mastercard',
      tier: 'Gold',
      fee: 'Rs. 2,500',
      apr: '38%',
      rewards: '1x Points',
      lounge: 'No'
    }, {
      id: 'platinum',
      name: 'BOP Platinum Mastercard',
      tier: 'Platinum',
      fee: 'Rs. 5,000',
      apr: '36%',
      rewards: '2x Points',
      lounge: 'Domestic Only'
    }, {
      id: 'world',
      name: 'BOP World Mastercard',
      tier: 'World',
      fee: 'Rs. 12,000',
      apr: '34%',
      rewards: '3x Points',
      lounge: 'Global Access'
    }];
    return <motion.div key="apply_choose_card" custom={direction} variants={slideVariants} initial="enter" animate="center" exit="exit" className={`w-full min-h-screen pb-8 flex flex-col ${isDark ? 'bg-[#1A0E08]' : 'bg-page-warm'}`} data-id="element-413">
        <Header title="Choose Your Card" onBackClick={() => navigateTo('apply_scheme', -1)} data-id="element-414" />
        <div className="px-6 mt-4 flex-1 overflow-y-auto" data-id="element-415">
          <p className={`text-[14px] mb-6 ${textSec}`} data-id="element-416">
            Select the card tier that best fits your lifestyle.
          </p>
          <div className="space-y-4" data-id="element-417">
            {cards.map(card => {
            const isSelected = selectedApplyCard?.id === card.id;
            const isExpanded = expandedApplyCard === card.id;
            return <div key={card.id} className={`rounded-2xl border-2 overflow-hidden transition-colors ${isSelected ? isDark ? 'border-[#F37024] bg-[#F37024]/5' : 'border-[#F37024] bg-[#F37024]/5' : cardSurface}`} data-id="element-418">
                  <button onClick={() => {
                setSelectedApplyCard(card);
                setExpandedApplyCard(isExpanded ? null : card.id);
              }} className="w-full p-4 text-left flex items-center justify-between" data-id="element-419">
                    <div data-id="element-420">
                      <p className={`text-[15px] font-bold ${textPri}`} data-id="element-421">
                        {card.name}
                      </p>
                      <p className={`text-[13px] ${textSec}`} data-id="element-422">
                        Annual Fee: {card.fee}
                      </p>
                    </div>
                    <div className="flex items-center gap-3" data-id="element-423">
                      <ChevronDownIcon className={`w-5 h-5 ${textSec} transition-transform ${isExpanded ? 'rotate-180' : ''}`} data-id="element-424" />
                      <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${isSelected ? 'border-[#F37024]' : isDark ? 'border-[#3D2518]' : 'border-[#E5D5C5]'}`} data-id="element-425">
                        {isSelected && <div className="w-2.5 h-2.5 rounded-full bg-[#F37024]" data-id="element-426" />}
                      </div>
                    </div>
                  </button>
                  <AnimatePresence data-id="element-427">
                    {isExpanded && <motion.div initial={{
                  height: 0,
                  opacity: 0
                }} animate={{
                  height: 'auto',
                  opacity: 1
                }} exit={{
                  height: 0,
                  opacity: 0
                }} className="overflow-hidden" data-id="element-428">
                        <div className="px-4 pb-4 pt-2 border-t border-black/5 dark:border-white/5" data-id="element-429">
                          <div className="grid grid-cols-2 gap-y-3 gap-x-4" data-id="element-430">
                            <div data-id="element-431">
                              <p className={`text-[11px] ${textSec}`} data-id="element-432">APR</p>
                              <p className={`text-[13px] font-bold ${textPri}`} data-id="element-433">
                                {card.apr}
                              </p>
                            </div>
                            <div data-id="element-434">
                              <p className={`text-[11px] ${textSec}`} data-id="element-435">
                                Rewards
                              </p>
                              <p className={`text-[13px] font-bold ${textPri}`} data-id="element-436">
                                {card.rewards}
                              </p>
                            </div>
                            <div className="col-span-2" data-id="element-437">
                              <p className={`text-[11px] ${textSec}`} data-id="element-438">
                                Lounge Access
                              </p>
                              <p className={`text-[13px] font-bold ${textPri}`} data-id="element-439">
                                {card.lounge}
                              </p>
                            </div>
                          </div>
                        </div>
                      </motion.div>}
                  </AnimatePresence>
                </div>;
          })}
          </div>
        </div>
        <div className="px-6 mt-auto pt-6" data-id="element-440">
          <button onClick={() => navigateTo('apply_name_delivery')} disabled={!selectedApplyCard} className="w-full py-4 bg-[#F37024] text-white font-bold text-[16px] rounded-2xl shadow-md active:scale-[0.98] transition-transform disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-441">
            Continue
          </button>
        </div>
      </motion.div>;
  };
  const renderApplyNameDelivery = () => <motion.div key="apply_name_delivery" custom={direction} variants={slideVariants} initial="enter" animate="center" exit="exit" className={`w-full min-h-screen pb-8 flex flex-col ${isDark ? 'bg-[#1A0E08]' : 'bg-page-warm'}`} data-id="element-442">
      <Header title="Name & Delivery" onBackClick={() => navigateTo('apply_choose_card', -1)} data-id="element-443" />
      <div className="px-6 mt-4 flex-1 overflow-y-auto" data-id="element-444">
        <div className="mb-6 pointer-events-none" data-id="element-445">
          <CreditCardVisual number="•••• •••• •••• ••••" cardholder={nameOnCard || 'YOUR NAME'} expires="MM/YY" data-id="element-446" />
        </div>
        <div className="mb-8" data-id="element-447">
          <label className={`block text-[13px] font-bold mb-2 ${textPri}`} data-id="element-448">
            Name on Card
          </label>
          <div className="relative" data-id="element-449">
            <input type="text" value={nameOnCard} onChange={e => setNameOnCard(e.target.value.toUpperCase().slice(0, 20))} placeholder="e.g. KASHISH" className={`w-full p-4 rounded-xl border-2 outline-none transition-colors ${isDark ? 'bg-[#2D1810] border-[#3D2518] text-white focus:border-[#F37024]' : 'bg-white border-[#E5D5C5] text-[#303030] focus:border-[#F37024]'}`} data-id="element-450" />
            <span className={`absolute right-4 top-4 text-[12px] ${textSec}`} data-id="element-451">
              {nameOnCard.length}/20
            </span>
          </div>
        </div>
        <div data-id="element-452">
          <label className={`block text-[13px] font-bold mb-3 ${textPri}`} data-id="element-453">
            Delivery Address
          </label>
          <div className="flex gap-3 mb-4" data-id="element-454">
            {['home', 'office'].map(type => <button key={type} onClick={() => setApplyAddressType(type as any)} className={`flex-1 py-3 rounded-xl border-2 text-[13px] font-bold capitalize transition-colors ${applyAddressType === type ? isDark ? 'border-[#F37024] text-[#F37024] bg-[#F37024]/10' : 'border-[#F37024] text-[#F37024] bg-[#F37024]/5' : isDark ? 'border-[#3D2518] text-[#B8A89E]' : 'border-[#E5D5C5] text-[#818285]'}`} data-id="element-455">
                {type}
              </button>)}
          </div>
          <textarea value={applyAddress} onChange={e => setApplyAddress(e.target.value)} placeholder="Enter full delivery address..." rows={3} className={`w-full p-4 rounded-xl border-2 outline-none resize-none transition-colors ${isDark ? 'bg-[#2D1810] border-[#3D2518] text-white focus:border-[#F37024]' : 'bg-white border-[#E5D5C5] text-[#303030] focus:border-[#F37024]'}`} data-id="element-456" />
        </div>
      </div>
      <div className="px-6 mt-auto pt-6" data-id="element-457">
        <button onClick={() => navigateTo('apply_review')} disabled={!nameOnCard || !applyAddress} className="w-full py-4 bg-[#F37024] text-white font-bold text-[16px] rounded-2xl shadow-md active:scale-[0.98] transition-transform disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-458">
          Continue
        </button>
      </div>
    </motion.div>;
  const renderApplyReview = () => {
    const acc = PAY_FROM_ACCOUNTS.find(a => a.id === selectedApplyAccount);
    return <motion.div key="apply_review" custom={direction} variants={slideVariants} initial="enter" animate="center" exit="exit" className={`w-full min-h-screen pb-8 flex flex-col ${isDark ? 'bg-[#1A0E08]' : 'bg-page-warm'}`} data-id="element-459">
        <Header title="Review Application" onBackClick={() => navigateTo('apply_name_delivery', -1)} data-id="element-460" />
        <div className="px-6 mt-4 flex-1 overflow-y-auto" data-id="element-461">
          <div className={`rounded-2xl p-5 mb-6 ${cardSurface}`} data-id="element-462">
            <h3 className={`text-[16px] font-bold mb-4 ${textPri}`} data-id="element-463">
              Application Summary
            </h3>
            <div className="space-y-4" data-id="element-464">
              <div className="flex justify-between" data-id="element-465">
                <span className={`text-[13px] ${textSec}`} data-id="element-466">Linked Account</span>
                <span className={`text-[13px] font-bold ${textPri}`} data-id="element-467">
                  {acc?.number}
                </span>
              </div>
              <div className="flex justify-between" data-id="element-468">
                <span className={`text-[13px] ${textSec}`} data-id="element-469">Card Type</span>
                <span className={`text-[13px] font-bold ${textPri}`} data-id="element-470">
                  {selectedApplyCard?.name}
                </span>
              </div>
              <div className="flex justify-between" data-id="element-471">
                <span className={`text-[13px] ${textSec}`} data-id="element-472">Name on Card</span>
                <span className={`text-[13px] font-bold ${textPri}`} data-id="element-473">
                  {nameOnCard}
                </span>
              </div>
              <div className="flex justify-between" data-id="element-474">
                <span className={`text-[13px] ${textSec}`} data-id="element-475">Annual Charges</span>
                <span className={`text-[13px] font-bold ${textPri}`} data-id="element-476">
                  {selectedApplyCard?.fee}
                </span>
              </div>
              <div className="pt-4 border-t border-black/5 dark:border-white/5" data-id="element-477">
                <span className={`block text-[13px] ${textSec} mb-1`} data-id="element-478">
                  Delivery Address ({applyAddressType})
                </span>
                <span className={`block text-[13px] font-bold ${textPri}`} data-id="element-479">
                  {applyAddress}
                </span>
              </div>
            </div>
          </div>
          <label className="flex items-start gap-3 cursor-pointer" data-id="element-480">
            <div className="mt-0.5" data-id="element-481">
              <div onClick={() => setApplyTermsAgreed(!applyTermsAgreed)} className={`w-6 h-6 rounded-md border-2 flex items-center justify-center transition-colors ${applyTermsAgreed ? 'bg-[#F37024] border-[#F37024]' : isDark ? 'border-[#3D2518]' : 'border-[#E5D5C5]'}`} data-id="element-482">
                {applyTermsAgreed && <CheckIcon className="w-4 h-4 text-white" strokeWidth={3} data-id="element-483" />}
              </div>
            </div>
            <p className={`text-[13px] leading-relaxed ${textSec}`} data-id="element-484">
              I agree to the Terms & Conditions and authorize the bank to deduct
              the annual fee from my linked account.
            </p>
          </label>
        </div>
        <div className="px-6 mt-auto pt-6" data-id="element-485">
          <button onClick={() => navigateTo('apply_processing')} disabled={!applyTermsAgreed} className="w-full py-4 bg-[#5A1408] text-white font-bold text-[16px] rounded-2xl shadow-md active:scale-[0.98] transition-transform disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-486">
            Submit Application
          </button>
        </div>
      </motion.div>;
  };
  const renderApplyProcessing = () => <motion.div key="apply_processing" custom={direction} variants={slideVariants} initial="enter" animate="center" exit="exit" className={`w-full min-h-screen flex flex-col items-center justify-center ${isDark ? 'bg-[#1A0E08]' : 'bg-page-warm'}`} data-id="element-487">
      <motion.div animate={{
      rotate: 360
    }} transition={{
      duration: 1,
      repeat: Infinity,
      ease: 'linear'
    }} className="mb-6" data-id="element-488">
        <RefreshCwIcon className="w-14 h-14 text-[#F37024]" strokeWidth={1.5} data-id="element-489" />
      </motion.div>
      <p className={`text-[20px] font-bold mb-2 ${textPri}`} data-id="element-490">
        Processing Application...
      </p>
    </motion.div>;
  const renderApplySuccess = () => <motion.div key="apply_success" custom={direction} variants={slideVariants} initial="enter" animate="center" exit="exit" className={`w-full min-h-screen pb-8 flex flex-col ${isDark ? 'bg-[#1A0E08]' : 'bg-page-warm'}`} data-id="element-491">
      <SuccessView title="Application Submitted" subtitle="Your card application has been received." actionText="Back to Cards" onAction={() => navigateTo('tabs', -1)} data-id="element-492" />
    </motion.div>;
  // ===== Screens =====
  const renderTabs = () => <motion.div key="tabs" custom={direction} variants={slideVariants} initial="enter" animate="center" exit="exit" className={`w-full min-h-screen pb-20 ${isDark ? 'bg-[#1A0E08]' : 'bg-page-warm'}`} data-id="element-493">
      <div className="px-6 pt-6 pb-4 flex items-center justify-between" data-id="element-494">
        <button onClick={onBack} aria-label="Back" className={`p-2 -ml-2 ${textPri}`} data-id="element-495">
          <ArrowLeftIcon className="w-6 h-6" strokeWidth={2} data-id="element-496" />
        </button>
        <h1 className={`text-[18px] font-bold ${textPri}`} data-id="element-497">My Cards</h1>
        <button onClick={onNavigateHome} aria-label="Home" className={`w-10 h-10 rounded-full border-2 flex items-center justify-center ${isDark ? 'border-[#F37024] text-[#F37024]' : 'border-[#5A1408] text-[#5A1408]'}`} data-id="element-498">
          <HomeIcon className="w-5 h-5" strokeWidth={2} data-id="element-499" />
        </button>
      </div>

      <div className="px-6 mt-2" data-id="element-500">
        <div className={`relative flex rounded-2xl p-1 ${isDark ? 'bg-[#2D1810]' : 'bg-[#F5EBE6]'}`} data-id="element-501">
          <motion.div layout className="absolute top-1 bottom-1 rounded-xl bg-[#F37024] shadow-md" initial={false} animate={{
          left: tab === 'credit' ? 4 : '50%',
          right: tab === 'credit' ? '50%' : 4
        }} transition={{
          type: 'spring',
          damping: 28,
          stiffness: 280
        }} data-id="element-502" />
          <button onClick={() => setTab('credit')} className={`relative z-10 flex-1 py-2.5 text-[13px] font-bold rounded-xl transition-colors ${tab === 'credit' ? 'text-white' : isDark ? 'text-[#B8A89E]' : 'text-[#818285]'}`} data-id="element-503">
            My Credit Cards
          </button>
          <button onClick={() => setTab('debit')} className={`relative z-10 flex-1 py-2.5 text-[13px] font-bold rounded-xl transition-colors ${tab === 'debit' ? 'text-white' : isDark ? 'text-[#B8A89E]' : 'text-[#818285]'}`} data-id="element-504">
            My Debit Cards
          </button>
        </div>
      </div>

      <div className="px-6 mt-6" data-id="element-505">
        <button onClick={() => navigateTo('apply_account')} className={`w-full py-3.5 rounded-2xl flex items-center justify-center gap-2 border-2 border-dashed ${isDark ? 'border-[#F37024]/40 text-[#F37024] hover:bg-[#F37024]/10' : 'border-[#5A1408]/40 text-[#5A1408] hover:bg-[#5A1408]/5'} transition-colors`} data-id="element-506">
          <PlusIcon className="w-5 h-5" strokeWidth={2.5} data-id="element-507" />
          <span className="text-[14px] font-bold" data-id="element-508">Apply for a New Card</span>
        </button>
      </div>

      <div className="mt-6" data-id="element-509">
        <div ref={carouselRef} className="flex overflow-x-auto snap-x snap-mandatory scrollbar-hide py-4" style={{
        paddingLeft: '50px',
        paddingRight: '50px',
        gap: '5px'
      }} data-id="element-510">
          {CAROUSEL_CARDS.filter(c => c.type === tab).map((card, i) => {
          const isActive = carouselActiveIndex === i;
          const isCardActive = cardStatuses[card.id];
          return <div key={card.id} ref={el => cardRefs.current[i] = el} className="snap-center shrink-0 w-[85%] transition-all duration-350 ease-out relative" style={{
            transform: isActive ? 'scale(1)' : 'scale(0.88)',
            opacity: isActive ? 1 : 0.55
          }} data-id="element-511">
                <div style={{
              opacity: isCardActive ? 1 : 0.55,
              transition: 'opacity 0.3s'
            }} data-id="element-512">
                  {card.type === 'credit' ? <CreditCardVisual number={card.number} cardholder="KASHISH" expires={card.expires} data-id="element-513" /> : <DebitCardVisual number={card.number} cardholder="KASHISH" expires={card.expires} tier={card.label} data-id="element-514" />}
                </div>
                {!isCardActive && !card.isNew && <div className="absolute inset-0 flex items-center justify-center pointer-events-none" data-id="element-515">
                    <div className="bg-black/60 backdrop-blur-sm rounded-full p-3" data-id="element-516">
                      <LockIcon className="w-7 h-7 text-white" strokeWidth={2.2} data-id="element-517" />
                    </div>
                  </div>}
              </div>;
        })}
        </div>
      </div>

      {(() => {
      const activeCards = CAROUSEL_CARDS.filter(c => c.type === tab);
      const activeCard = activeCards[carouselActiveIndex];
      if (!activeCard) return null;
      const isCardActive = cardStatuses[activeCard.id];
      return <div className="px-6 mt-2" data-id="element-518">
            <div className={`flex flex-col gap-4 rounded-2xl px-4 py-4 ${cardSurface}`} data-id="element-519">
              <div className="flex items-center justify-between" data-id="element-520">
                <div className="flex items-center gap-3" data-id="element-521">
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{
                background: isCardActive ? 'rgba(16,185,129,0.12)' : 'rgba(239,68,68,0.10)'
              }} data-id="element-522">
                    {isCardActive ? <UnlockIcon className="w-5 h-5 text-[#10B981]" strokeWidth={2} data-id="element-523" /> : <LockIcon className="w-5 h-5 text-[#EF4444]" strokeWidth={2} data-id="element-524" />}
                  </div>
                  <div data-id="element-525">
                    <p className={`text-[14px] font-bold ${textPri}`} data-id="element-526">
                      {isCardActive ? 'Active' : 'Inactive'}
                    </p>
                    <p className={`text-[11px] ${textSec}`} data-id="element-527">
                      {isCardActive ? 'Card is ready to use' : activeCard.inactiveReason || 'Card is locked'}
                    </p>
                  </div>
                </div>
                {!activeCard.isNew && <button onClick={() => setCardStatuses(prev => ({
              ...prev,
              [activeCard.id]: !prev[activeCard.id]
            }))} aria-pressed={isCardActive} className="relative w-12 h-7 rounded-full transition-colors" style={{
              background: isCardActive ? '#10B981' : '#EF4444'
            }} data-id="element-528">
                    <motion.div className="absolute top-1 w-5 h-5 rounded-full bg-white shadow-md" animate={{
                left: isCardActive ? 24 : 4
              }} transition={{
                type: 'spring',
                stiffness: 500,
                damping: 30
              }} data-id="element-529" />
                  </button>}
              </div>
              {activeCard.isNew && !isCardActive && <button onClick={() => {
            setSelectedCard(CREDIT_CARD); // Reuse existing activation flow with mock card
            navigateTo('activation_details');
          }} className="w-full py-3 bg-[#F37024] text-white font-bold text-[14px] rounded-xl shadow-md active:scale-[0.98] transition-transform" data-id="element-530">
                  Activate Card
                </button>}
            </div>
          </div>;
    })()}

      {tab === 'credit' && <div className="px-6 mt-6 grid grid-cols-2 gap-3" data-id="element-531">
          <button onClick={() => {
        setSelectedCard(CREDIT_CARD);
        navigateTo('summary');
      }} disabled={isLocked} className={`rounded-2xl py-4 px-3 flex flex-col items-center gap-2 shadow-sm active:scale-[0.98] transition-all disabled:opacity-50 disabled:cursor-not-allowed ${cardSurface}`} data-id="element-532">
            <ReceiptIcon className="w-6 h-6 text-[#F37024]" strokeWidth={2} data-id="element-533" />
            <span className={`text-[13px] font-bold ${textPri}`} data-id="element-534">
              Card Summary
            </span>
          </button>
          <button onClick={() => {
        setSelectedCard(CREDIT_CARD);
        navigateTo('manage_cards');
      }} disabled={isLocked} className={`rounded-2xl py-4 px-3 flex flex-col items-center gap-2 shadow-sm active:scale-[0.98] transition-all disabled:opacity-50 disabled:cursor-not-allowed ${cardSurface}`} data-id="element-535">
            <SettingsIcon className="w-6 h-6 text-[#F37024]" strokeWidth={2} data-id="element-536" />
            <span className={`text-[13px] font-bold ${textPri}`} data-id="element-537">
              Manage Card
            </span>
          </button>
        </div>}
      {tab === 'debit' && <div className="px-6 mt-6" data-id="element-538">
          <button onClick={() => {
        setSelectedCard(DEBIT_CARDS[0]);
        navigateTo('manage_cards');
      }} className={`w-full rounded-2xl py-4 px-3 flex items-center justify-center gap-2 shadow-sm active:scale-[0.98] transition-all ${cardSurface}`} data-id="element-539">
            <SettingsIcon className="w-6 h-6 text-[#F37024]" strokeWidth={2} data-id="element-540" />
            <span className={`text-[14px] font-bold ${textPri}`} data-id="element-541">
              Manage Debit Cards
            </span>
          </button>
        </div>}
    </motion.div>;
  const renderSummary = () => {
    const utilization = (CREDIT_CARD.totalLimit - CREDIT_CARD.availableLimit) / CREDIT_CARD.totalLimit * 100;
    return <motion.div key="summary" custom={direction} variants={slideVariants} initial="enter" animate="center" exit="exit" className={`w-full min-h-screen pb-8 ${isDark ? 'bg-[#1A0E08]' : 'bg-page-warm'}`} data-id="element-542">
        <Header title="Card Summary" data-id="element-543" />
        <div className="px-6 space-y-5" data-id="element-544">
          <CreditCardVisual number={CREDIT_CARD.number} cardholder={CREDIT_CARD.cardholder} expires={CREDIT_CARD.expires} data-id="element-545" />

          <div className={`rounded-2xl p-5 ${cardSurface}`} data-id="element-546">
            <div className="flex items-baseline justify-between mb-2" data-id="element-547">
              <p className={`text-[12px] font-bold uppercase tracking-wider ${isDark ? 'text-[#F37024]' : 'text-[#5A1408]'}`} data-id="element-548">
                Utilization
              </p>
              <p className={`text-[13px] font-bold ${textPri}`} data-id="element-549">
                {utilization.toFixed(0)}%
              </p>
            </div>
            <div className={`relative h-2.5 rounded-full overflow-hidden ${isDark ? 'bg-[#3D2518]' : 'bg-[#F5EBE6]'}`} data-id="element-550">
              <motion.div initial={{
              width: 0
            }} animate={{
              width: `${utilization}%`
            }} transition={{
              duration: 0.8,
              ease: 'easeOut'
            }} className="absolute inset-y-0 left-0 rounded-full" style={{
              background: 'linear-gradient(90deg, #F37024 0%, #D95A1A 100%)'
            }} data-id="element-551" />
            </div>
            <div className="flex justify-between mt-3" data-id="element-552">
              <div data-id="element-553">
                <p className={`text-[10px] uppercase tracking-wider ${textSec}`} data-id="element-554">
                  Used
                </p>
                <p className={`text-[13px] font-bold ${textPri}`} data-id="element-555">
                  PKR{' '}
                  {formatPKR(CREDIT_CARD.totalLimit - CREDIT_CARD.availableLimit)}
                </p>
              </div>
              <div className="text-right" data-id="element-556">
                <p className={`text-[10px] uppercase tracking-wider ${textSec}`} data-id="element-557">
                  Available
                </p>
                <p className={`text-[13px] font-bold text-[#10B981]`} data-id="element-558">
                  PKR {formatPKR(CREDIT_CARD.availableLimit)}
                </p>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3" data-id="element-559">
            <div className={`rounded-2xl p-4 ${cardSurface}`} data-id="element-560">
              <p className={`text-[10px] uppercase tracking-wider ${textSec}`} data-id="element-561">
                Total Amount Due
              </p>
              <p className={`text-[16px] font-bold mt-1 text-[#EF4444]`} data-id="element-562">
                PKR {formatPKR(CREDIT_CARD.totalDue)}
              </p>
            </div>
            <div className={`rounded-2xl p-4 ${cardSurface}`} data-id="element-563">
              <p className={`text-[10px] uppercase tracking-wider ${textSec}`} data-id="element-564">
                Minimum Amount Due
              </p>
              <p className={`text-[16px] font-bold mt-1 ${textPri}`} data-id="element-565">
                PKR {formatPKR(CREDIT_CARD.minDue)}
              </p>
            </div>
          </div>

          <button onClick={() => {
          setPayAmount('');
          navigateTo('pay_bill');
        }} className="w-full py-4 bg-[#5A1408] text-white font-bold text-[16px] rounded-2xl shadow-md active:scale-[0.98] transition-transform flex items-center justify-center gap-2" data-id="element-566">
            Pay Bill <ArrowRightIcon className="w-5 h-5" strokeWidth={2.5} data-id="element-567" />
          </button>

          <div data-id="element-568">
            <h3 className={`text-[16px] font-bold mb-3 ${textPri}`} data-id="element-569">
              Recent Transactions
            </h3>
            <div className={`rounded-2xl overflow-hidden ${cardSurface}`} data-id="element-570">
              {TRANSACTIONS.map((tx, i) => <div key={tx.id} className={`p-4 flex items-center justify-between ${i !== TRANSACTIONS.length - 1 ? 'border-b border-[rgba(0,0,0,0.05)]' : ''}`} data-id="element-571">
                  <div className="flex items-center gap-3" data-id="element-572">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${isDark ? 'bg-[#3D2518]' : 'bg-[#F5EBE6]'}`} data-id="element-573">
                      {tx.type === 'credit' ? <ArrowLeftIcon className="w-5 h-5 text-[#10B981]" data-id="element-574" /> : <ReceiptIcon className="w-5 h-5 text-[#F37024]" data-id="element-575" />}
                    </div>
                    <div data-id="element-576">
                      <p className={`text-[14px] font-bold ${textPri}`} data-id="element-577">
                        {tx.merchant}
                      </p>
                      <p className={`text-[11px] ${textSec}`} data-id="element-578">{tx.date}</p>
                    </div>
                  </div>
                  <p className={`text-[14px] font-bold ${tx.type === 'credit' ? 'text-[#10B981]' : textPri}`} data-id="element-579">
                    {tx.type === 'credit' ? '+' : '-'} PKR{' '}
                    {formatPKR(tx.amount)}
                  </p>
                </div>)}
            </div>
          </div>
        </div>
      </motion.div>;
  };
  const renderManageCards = () => <motion.div key="manage_cards" custom={direction} variants={slideVariants} initial="enter" animate="center" exit="exit" className={`w-full min-h-screen pb-8 ${isDark ? 'bg-[#1A0E08]' : 'bg-page-warm'}`} data-id="element-580">
      <Header title="Manage Cards" data-id="element-581" />
      <div className="px-6 space-y-6" data-id="element-582">
        <div data-id="element-583">
          <h3 className={`text-[14px] font-bold uppercase tracking-wider mb-3 ${textSec}`} data-id="element-584">
            Physical Cards
          </h3>
          <div className="space-y-3" data-id="element-585">
            {[CREDIT_CARD, ...DEBIT_CARDS].map(card => <button key={card.id} onClick={() => {
            setSelectedCard(card);
            navigateTo('card_detail');
          }} className={`w-full p-4 rounded-2xl flex items-center justify-between active:scale-[0.98] transition-transform ${cardSurface}`} data-id="element-586">
                <div className="flex items-center gap-3" data-id="element-587">
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${isDark ? 'bg-[#3D2518]' : 'bg-[#F5EBE6]'}`} data-id="element-588">
                    <CreditCardIcon className="w-6 h-6 text-[#F37024]" data-id="element-589" />
                  </div>
                  <div className="text-left" data-id="element-590">
                    <p className={`text-[14px] font-bold ${textPri}`} data-id="element-591">
                      {card.type} • {card.tier}
                    </p>
                    <p className={`text-[12px] ${textSec}`} data-id="element-592">{card.number}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2" data-id="element-593">
                  <span className={`px-2 py-1 rounded-md text-[10px] font-bold ${card.status === 'Active' ? 'bg-[#10B981]/10 text-[#10B981]' : 'bg-[#EF4444]/10 text-[#EF4444]'}`} data-id="element-594">
                    {card.status}
                  </span>
                  <ChevronRightIcon className={`w-5 h-5 ${textSec}`} data-id="element-595" />
                </div>
              </button>)}
          </div>
        </div>

        <div data-id="element-596">
          <h3 className={`text-[14px] font-bold uppercase tracking-wider mb-3 ${textSec}`} data-id="element-597">
            Virtual Cards
          </h3>
          <div className="space-y-3" data-id="element-598">
            {VIRTUAL_CARDS.map(card => <button key={card.id} onClick={() => {
            setSelectedCard(card);
            navigateTo('card_detail');
          }} className={`w-full p-4 rounded-2xl flex items-center justify-between active:scale-[0.98] transition-transform ${cardSurface}`} data-id="element-599">
                <div className="flex items-center gap-3" data-id="element-600">
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${isDark ? 'bg-[#3D2518]' : 'bg-[#F5EBE6]'}`} data-id="element-601">
                    <SmartphoneIcon className="w-6 h-6 text-[#F37024]" data-id="element-602" />
                  </div>
                  <div className="text-left" data-id="element-603">
                    <p className={`text-[14px] font-bold ${textPri}`} data-id="element-604">
                      {card.type} • {card.tier}
                    </p>
                    <p className={`text-[12px] ${textSec}`} data-id="element-605">{card.number}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2" data-id="element-606">
                  <span className="px-2 py-1 rounded-md text-[10px] font-bold bg-[#10B981]/10 text-[#10B981]" data-id="element-607">
                    {card.status}
                  </span>
                  <ChevronRightIcon className={`w-5 h-5 ${textSec}`} data-id="element-608" />
                </div>
              </button>)}
          </div>
        </div>
      </div>
    </motion.div>;
  const renderCardDetail = () => <motion.div key="card_detail" custom={direction} variants={slideVariants} initial="enter" animate="center" exit="exit" className={`w-full min-h-screen pb-8 ${isDark ? 'bg-[#1A0E08]' : 'bg-page-warm'}`} data-id="element-609">
      <Header title="Card Details" onBackClick={() => navigateTo('manage_cards', -1)} data-id="element-610" />
      <div className="px-6 space-y-6" data-id="element-611">
        {selectedCard.type === 'CREDIT' && <CreditCardVisual {...selectedCard} showFull={showVirtualDetails} data-id="element-612" />}
        {selectedCard.type === 'DEBIT' && <DebitCardVisual {...selectedCard} showFull={showVirtualDetails} data-id="element-613" />}
        {selectedCard.type === 'VIRTUAL' && <VirtualCardVisual {...selectedCard} showFull={showVirtualDetails} data-id="element-614" />}

        {selectedCard.type === 'VIRTUAL' && <button onClick={() => setShowVirtualDetails(!showVirtualDetails)} className={`w-full py-3 rounded-xl flex items-center justify-center gap-2 font-bold ${cardSurface} ${textPri}`} data-id="element-615">
            {showVirtualDetails ? <EyeOffIcon className="w-5 h-5" data-id="element-616" /> : <EyeIcon className="w-5 h-5" data-id="element-617" />}
            {showVirtualDetails ? 'Hide Details' : 'Reveal Details'}
          </button>}

        <div className="grid grid-cols-4 gap-3" data-id="element-618">
          <button onClick={() => navigateTo('card_controls')} className={`p-3 rounded-2xl flex flex-col items-center justify-center gap-2 ${cardSurface}`} data-id="element-619">
            <SlidersIcon className="w-6 h-6 text-[#F37024]" data-id="element-620" />
            <span className={`text-[11px] font-bold text-center ${textPri}`} data-id="element-621">
              Controls
            </span>
          </button>
          <button onClick={() => navigateTo('spending_limits')} className={`p-3 rounded-2xl flex flex-col items-center justify-center gap-2 ${cardSurface}`} data-id="element-622">
            <ShieldIcon className="w-6 h-6 text-[#F37024]" data-id="element-623" />
            <span className={`text-[11px] font-bold text-center ${textPri}`} data-id="element-624">
              Limits
            </span>
          </button>
          <button onClick={() => {
          setPinInput('');
          navigateTo('pin_management');
        }} className={`p-3 rounded-2xl flex flex-col items-center justify-center gap-2 ${cardSurface}`} data-id="element-625">
            <LockIcon className="w-6 h-6 text-[#F37024]" data-id="element-626" />
            <span className={cn(`text-[11px] font-bold text-center ${textPri}`, 'pb-[0px] mb-[0px]')} data-id="element-627">
              Change PIN
            </span>
          </button>
          <button onClick={() => navigateTo('block_entry')} className={`p-3 rounded-2xl flex flex-col items-center justify-center gap-2 ${cardSurface}`} data-id="element-628">
            <AlertCircleIcon className="w-6 h-6 text-[#EF4444]" data-id="element-629" />
            <span className={`text-[11px] font-bold text-center ${textPri}`} data-id="element-630">
              Block
            </span>
          </button>
        </div>

        <div className={`rounded-2xl overflow-hidden ${cardSurface}`} data-id="element-631">
          <button onClick={() => navigateTo('activation_entry')} className="w-full p-4 flex items-center justify-between" data-id="element-632">
            <div className="flex items-center gap-3" data-id="element-633">
              <CheckIcon className={`w-5 h-5 ${textSec}`} data-id="element-634" />
              <span className={`text-[14px] font-bold ${textPri}`} data-id="element-635">
                Activate Card
              </span>
            </div>
            <ChevronRightIcon className={`w-5 h-5 ${textSec}`} data-id="element-636" />
          </button>
        </div>
      </div>
    </motion.div>;
  const renderCardControls = () => <motion.div key="card_controls" custom={direction} variants={slideVariants} initial="enter" animate="center" exit="exit" className={`w-full min-h-screen pb-8 ${isDark ? 'bg-[#1A0E08]' : 'bg-page-warm'}`} data-id="element-637">
      <Header title="Card Controls" onBackClick={() => navigateTo('card_detail', -1)} data-id="element-638" />
      <div className="px-6 space-y-4" data-id="element-639">
        {[{
        id: 'ecommerce',
        label: 'E-Commerce',
        desc: 'Online purchases',
        icon: GlobeIcon
      }, {
        id: 'contactless',
        label: 'Contactless',
        desc: 'Tap to pay',
        icon: WifiIcon
      }, {
        id: 'international',
        label: 'International',
        desc: 'Use abroad',
        icon: MapPinIcon
      }, {
        id: 'atm',
        label: 'ATM Withdrawals',
        desc: 'Cash from ATMs',
        icon: CreditCardIcon
      }].map(ctrl => <div key={ctrl.id} className={`p-4 rounded-2xl flex items-center justify-between ${cardSurface}`} data-id="element-640">
            <div className="flex items-center gap-3" data-id="element-641">
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${isDark ? 'bg-[#3D2518]' : 'bg-[#F5EBE6]'}`} data-id="element-642">
                <ctrl.icon className="w-5 h-5 text-[#F37024]" data-id="element-643" />
              </div>
              <div data-id="element-644">
                <p className={`text-[14px] font-bold ${textPri}`} data-id="element-645">
                  {ctrl.label}
                </p>
                <p className={`text-[11px] ${textSec}`} data-id="element-646">{ctrl.desc}</p>
              </div>
            </div>
            <button onClick={() => {
          setPendingToggle(ctrl.id);
          navigateTo('toggle_confirm');
        }} className="relative w-12 h-7 rounded-full transition-colors" style={{
          background: toggles[ctrl.id as keyof typeof toggles] ? '#10B981' : '#EF4444'
        }} data-id="element-647">
              <motion.div className="absolute top-1 w-5 h-5 rounded-full bg-white shadow-md" animate={{
            left: toggles[ctrl.id as keyof typeof toggles] ? 24 : 4
          }} transition={{
            type: 'spring',
            stiffness: 500,
            damping: 30
          }} data-id="element-648" />
            </button>
          </div>)}
      </div>
    </motion.div>;
  const renderToggleConfirm = () => <motion.div key="toggle_confirm" custom={direction} variants={slideVariants} initial="enter" animate="center" exit="exit" className={`w-full min-h-screen pb-8 flex flex-col ${isDark ? 'bg-[#1A0E08]' : 'bg-page-warm'}`} data-id="element-649">
      <Header title="Confirm Change" onBackClick={() => navigateTo('card_controls', -1)} data-id="element-650" />
      <div className="px-6 flex-1 flex flex-col justify-center items-center text-center" data-id="element-651">
        <AlertCircleIcon className="w-16 h-16 text-[#F37024] mb-4" data-id="element-652" />
        <h2 className={`text-[20px] font-bold mb-2 ${textPri}`} data-id="element-653">
          Are you sure?
        </h2>
        <p className={`text-[14px] mb-8 ${textSec}`} data-id="element-654">
          You are about to{' '}
          {toggles[pendingToggle as keyof typeof toggles] ? 'disable' : 'enable'}{' '}
          {pendingToggle} transactions for this card.
        </p>
        <div className="w-full space-y-3" data-id="element-655">
          <button onClick={() => {
          setToggles(prev => ({
            ...prev,
            [pendingToggle!]: !prev[pendingToggle as keyof typeof toggles]
          }));
          navigateTo('toggle_success');
        }} className="w-full py-4 bg-[#5A1408] text-white font-bold text-[16px] rounded-2xl shadow-md active:scale-[0.98] transition-transform" data-id="element-656">
            Confirm
          </button>
          <button onClick={() => navigateTo('card_controls', -1)} className={`w-full py-4 font-bold text-[16px] rounded-2xl ${textPri}`} data-id="element-657">
            Cancel
          </button>
        </div>
      </div>
    </motion.div>;
  const BLOCK_REASONS = ['Lost', 'Stolen', 'Damaged', 'Fraudulent activity', 'Other'];
  const renderBlockEntry = () => <motion.div key="block_entry" custom={direction} variants={slideVariants} initial="enter" animate="center" exit="exit" className={`w-full min-h-screen pb-8 flex flex-col ${isDark ? 'bg-[#1A0E08]' : 'bg-page-warm'}`} data-id="element-658">
      <Header title="Block Card" onBackClick={() => navigateTo('card_detail', -1)} data-id="element-659" />
      <div className="px-6 flex-1 flex flex-col" data-id="element-660">
        <div className={`p-5 rounded-2xl mb-6 ${cardSurface}`} data-id="element-661">
          <div className="flex items-center gap-3" data-id="element-662">
            <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${isDark ? 'bg-[#3D2518]' : 'bg-[#F5EBE6]'}`} data-id="element-663">
              <CreditCardIcon className="w-6 h-6 text-[#F37024]" data-id="element-664" />
            </div>
            <div data-id="element-665">
              <p className={`text-[14px] font-bold ${textPri}`} data-id="element-666">
                {selectedCard?.type} • {selectedCard?.tier}
              </p>
              <p className={`text-[12px] ${textSec}`} data-id="element-667">{selectedCard?.number}</p>
            </div>
          </div>
        </div>
        <div className={`p-4 rounded-2xl flex items-start gap-3 mb-6 ${isDark ? 'bg-[#3D1A12]' : 'bg-[#FEF2F2]'}`} data-id="element-668">
          <AlertCircleIcon className="w-6 h-6 text-[#EF4444] shrink-0" data-id="element-669" />
          <p className={`text-[13px] ${textSec}`} data-id="element-670">
            Blocking this card will immediately stop all transactions. You can
            request a replacement afterwards.
          </p>
        </div>
        <div className="mt-auto" data-id="element-671">
          <button onClick={() => navigateTo('block_reason')} className="w-full py-4 bg-[#5A1408] text-white font-bold text-[16px] rounded-2xl shadow-md active:scale-[0.98] transition-transform" data-id="element-672">
            Continue
          </button>
        </div>
      </div>
    </motion.div>;
  const renderBlockReason = () => <motion.div key="block_reason" custom={direction} variants={slideVariants} initial="enter" animate="center" exit="exit" className={`w-full min-h-screen pb-8 flex flex-col ${isDark ? 'bg-[#1A0E08]' : 'bg-page-warm'}`} data-id="element-673">
      <Header title="Reason for Blocking" onBackClick={() => navigateTo('block_entry', -1)} data-id="element-674" />
      <div className="px-6 space-y-3" data-id="element-675">
        {BLOCK_REASONS.map(reason => <button key={reason} onClick={() => {
        setBlockReason(reason);
        navigateTo('block_confirm');
      }} className={`w-full p-4 rounded-2xl flex items-center justify-between active:scale-[0.98] transition-transform ${cardSurface}`} data-id="element-676">
            <span className={`text-[14px] font-bold ${textPri}`} data-id="element-677">{reason}</span>
            <ChevronRightIcon className={`w-5 h-5 ${textSec}`} data-id="element-678" />
          </button>)}
      </div>
    </motion.div>;
  const renderBlockConfirm = () => <motion.div key="block_confirm" custom={direction} variants={slideVariants} initial="enter" animate="center" exit="exit" className={`w-full min-h-screen pb-8 flex flex-col ${isDark ? 'bg-[#1A0E08]' : 'bg-page-warm'}`} data-id="element-679">
      <Header title="Confirm Block" onBackClick={() => navigateTo('block_reason', -1)} data-id="element-680" />
      <div className="px-6 flex-1 flex flex-col justify-center items-center text-center" data-id="element-681">
        <AlertCircleIcon className="w-16 h-16 text-[#EF4444] mb-4" data-id="element-682" />
        <h2 className={`text-[20px] font-bold mb-2 ${textPri}`} data-id="element-683">
          Block this card?
        </h2>
        <p className={`text-[14px] mb-8 ${textSec}`} data-id="element-684">
          {selectedCard?.type} • {selectedCard?.tier} will be blocked
          {blockReason ? ` due to: ${blockReason}` : ''}. This cannot be undone.
        </p>
        <div className="w-full space-y-3" data-id="element-685">
          <button onClick={() => navigateTo('block_success')} className="w-full py-4 bg-[#EF4444] text-white font-bold text-[16px] rounded-2xl shadow-md active:scale-[0.98] transition-transform" data-id="element-686">
            Block Card
          </button>
          <button onClick={() => navigateTo('card_detail', -1)} className={`w-full py-4 font-bold text-[16px] rounded-2xl ${textPri}`} data-id="element-687">
            Cancel
          </button>
        </div>
      </div>
    </motion.div>;
  const renderSpendingLimits = () => <motion.div key="spending_limits" custom={direction} variants={slideVariants} initial="enter" animate="center" exit="exit" className={`w-full min-h-screen pb-8 ${isDark ? 'bg-[#1A0E08]' : 'bg-page-warm'}`} data-id="element-688">
      <Header title="Spending Limits" onBackClick={() => navigateTo('card_detail', -1)} data-id="element-689" />
      <div className="px-6 space-y-6" data-id="element-690">
        <div className={`p-5 rounded-2xl ${cardSurface}`} data-id="element-691">
          <div className="flex justify-between mb-4" data-id="element-692">
            <p className={`text-[14px] font-bold ${textPri}`} data-id="element-693">
              Daily ATM Limit
            </p>
            <p className={`text-[14px] font-bold text-[#F37024]`} data-id="element-694">PKR 50,000</p>
          </div>
          <input type="range" min="0" max="100000" step="5000" defaultValue="50000" className="w-full accent-[#F37024]" data-id="element-695" />
          <div className="flex justify-between mt-2" data-id="element-696">
            <span className={`text-[10px] ${textSec}`} data-id="element-697">0</span>
            <span className={`text-[10px] ${textSec}`} data-id="element-698">100,000</span>
          </div>
        </div>
        <div className={`p-5 rounded-2xl ${cardSurface}`} data-id="element-699">
          <div className="flex justify-between mb-4" data-id="element-700">
            <p className={`text-[14px] font-bold ${textPri}`} data-id="element-701">
              Daily POS Limit
            </p>
            <p className={`text-[14px] font-bold text-[#F37024]`} data-id="element-702">
              PKR 150,000
            </p>
          </div>
          <input type="range" min="0" max="500000" step="10000" defaultValue="150000" className="w-full accent-[#F37024]" data-id="element-703" />
          <div className="flex justify-between mt-2" data-id="element-704">
            <span className={`text-[10px] ${textSec}`} data-id="element-705">0</span>
            <span className={`text-[10px] ${textSec}`} data-id="element-706">500,000</span>
          </div>
        </div>
        <button onClick={() => navigateTo('card_detail', -1)} className="w-full py-4 bg-[#5A1408] text-white font-bold text-[16px] rounded-2xl shadow-md active:scale-[0.98] transition-transform" data-id="element-707">
          Save Changes
        </button>
      </div>
    </motion.div>;
  const renderBlockedMerchants = () => <motion.div key="blocked_merchants" custom={direction} variants={slideVariants} initial="enter" animate="center" exit="exit" className={`w-full min-h-screen pb-8 ${isDark ? 'bg-[#1A0E08]' : 'bg-page-warm'}`} data-id="element-708">
      <Header title="Blocked Merchants" onBackClick={() => navigateTo('card_detail', -1)} data-id="element-709" />
      <div className="px-6 space-y-4" data-id="element-710">
        {BLOCKED_MERCHANTS.map(m => <div key={m.id} className={`p-4 rounded-2xl flex items-center justify-between ${cardSurface}`} data-id="element-711">
            <div data-id="element-712">
              <p className={`text-[14px] font-bold ${textPri}`} data-id="element-713">{m.name}</p>
              <p className={`text-[11px] ${textSec}`} data-id="element-714">Blocked on {m.date}</p>
            </div>
            <button className="px-4 py-2 rounded-xl bg-[#10B981]/10 text-[#10B981] text-[12px] font-bold" data-id="element-715">
              Unblock
            </button>
          </div>)}
      </div>
    </motion.div>;
  const renderPinManagement = () => <motion.div key="pin_management" custom={direction} variants={slideVariants} initial="enter" animate="center" exit="exit" className={`w-full min-h-screen pb-8 flex flex-col ${isDark ? 'bg-[#1A0E08]' : 'bg-page-warm'}`} data-id="element-716">
      <Header title="PIN Management" onBackClick={() => navigateTo('card_detail', -1)} data-id="element-717" />
      <PinEntry title="Enter Current PIN" subtitle="Please enter your current 4-digit card PIN" onComplete={() => {
      setPinInput('');
      navigateTo('pin_enter_new');
    }} data-id="element-718" />
    </motion.div>;
  const renderPinEnterNew = () => <motion.div key="pin_enter_new" custom={direction} variants={slideVariants} initial="enter" animate="center" exit="exit" className={`w-full min-h-screen pb-8 flex flex-col ${isDark ? 'bg-[#1A0E08]' : 'bg-page-warm'}`} data-id="element-719">
      <Header title="New PIN" onBackClick={() => {
      setPinInput('');
      navigateTo('pin_management', -1);
    }} data-id="element-720" />
      <PinEntry title="Enter New PIN" subtitle="Create a new 4-digit PIN for your card" onComplete={() => {
      setPinInput('');
      navigateTo('pin_confirm_new');
    }} data-id="element-721" />
    </motion.div>;
  const renderPinConfirmNew = () => <motion.div key="pin_confirm_new" custom={direction} variants={slideVariants} initial="enter" animate="center" exit="exit" className={`w-full min-h-screen pb-8 flex flex-col ${isDark ? 'bg-[#1A0E08]' : 'bg-page-warm'}`} data-id="element-722">
      <Header title="Confirm PIN" onBackClick={() => {
      setPinInput('');
      navigateTo('pin_enter_new', -1);
    }} data-id="element-723" />
      <PinEntry title="Confirm New PIN" subtitle="Re-enter your new 4-digit PIN" onComplete={() => navigateTo('pin_success')} data-id="element-724" />
    </motion.div>;
  const renderActivationEntry = () => <motion.div key="activation_entry" custom={direction} variants={slideVariants} initial="enter" animate="center" exit="exit" className={`w-full min-h-screen pb-8 flex flex-col ${isDark ? 'bg-[#1A0E08]' : 'bg-page-warm'}`} data-id="element-725">
      <Header title="Activate Card" onBackClick={() => navigateTo('card_detail', -1)} data-id="element-726" />
      <div className="px-6 flex-1 flex flex-col justify-center items-center text-center" data-id="element-727">
        <CreditCardIcon className="w-16 h-16 text-[#F37024] mb-4" data-id="element-728" />
        <h2 className={`text-[20px] font-bold mb-2 ${textPri}`} data-id="element-729">
          Activate Your Card
        </h2>
        <p className={`text-[14px] mb-8 ${textSec}`} data-id="element-730">
          You'll need your physical card to complete this process.
        </p>
        <button onClick={() => navigateTo('activation_details')} className="w-full py-4 bg-[#5A1408] text-white font-bold text-[16px] rounded-2xl shadow-md active:scale-[0.98] transition-transform" data-id="element-731">
          Start Activation
        </button>
      </div>
    </motion.div>;
  const renderActivationDetails = () => <motion.div key="activation_details" custom={direction} variants={slideVariants} initial="enter" animate="center" exit="exit" className={`w-full min-h-screen pb-8 flex flex-col ${isDark ? 'bg-[#1A0E08]' : 'bg-page-warm'}`} data-id="element-732">
      <Header title="Card Details" onBackClick={() => navigateTo('activation_entry', -1)} data-id="element-733" />
      <div className="px-6 space-y-4 mt-4" data-id="element-734">
        <div className={`p-4 rounded-2xl ${cardSurface}`} data-id="element-735">
          <label className={`text-[12px] font-bold uppercase tracking-wider ${textSec}`} data-id="element-736">
            Expiry Date (MM/YY)
          </label>
          <input type="text" placeholder="MM/YY" className={`w-full mt-2 bg-transparent text-[16px] font-bold outline-none ${textPri}`} data-id="element-737" />
        </div>
        <div className={`p-4 rounded-2xl ${cardSurface}`} data-id="element-738">
          <label className={`text-[12px] font-bold uppercase tracking-wider ${textSec}`} data-id="element-739">
            CVV
          </label>
          <input type="password" placeholder="***" maxLength={3} className={`w-full mt-2 bg-transparent text-[16px] font-bold outline-none ${textPri}`} data-id="element-740" />
        </div>
        <button onClick={() => {
        setPinInput('');
        navigateTo('activation_set_pin');
      }} className="w-full py-4 mt-4 bg-[#5A1408] text-white font-bold text-[16px] rounded-2xl shadow-md active:scale-[0.98] transition-transform" data-id="element-741">
          Next
        </button>
      </div>
    </motion.div>;
  const renderActivationSetPin = () => <motion.div key="activation_set_pin" custom={direction} variants={slideVariants} initial="enter" animate="center" exit="exit" className={`w-full min-h-screen pb-8 flex flex-col ${isDark ? 'bg-[#1A0E08]' : 'bg-page-warm'}`} data-id="element-742">
      <Header title="Set PIN" onBackClick={() => navigateTo('activation_details', -1)} data-id="element-743" />
      <PinEntry title="Set Card PIN" subtitle="Create a 4-digit PIN for your new card" onComplete={() => navigateTo('activation_success')} data-id="element-744" />
    </motion.div>;
  const renderRequestEntry = () => <motion.div key="request_entry" custom={direction} variants={slideVariants} initial="enter" animate="center" exit="exit" className={`w-full min-h-screen pb-8 flex flex-col ${isDark ? 'bg-[#1A0E08]' : 'bg-page-warm'}`} data-id="element-745">
      <Header title="Request Card" onBackClick={() => navigateTo('manage_cards', -1)} data-id="element-746" />
      <div className="px-6 space-y-4 mt-4" data-id="element-747">
        <button onClick={() => navigateTo('request_type')} className={`w-full p-5 rounded-2xl flex items-center justify-between text-left ${cardSurface}`} data-id="element-748">
          <div data-id="element-749">
            <p className={`text-[16px] font-bold ${textPri}`} data-id="element-750">Debit Card</p>
            <p className={`text-[12px] mt-1 ${textSec}`} data-id="element-751">
              Get a new physical debit card
            </p>
          </div>
          <ChevronRightIcon className={`w-5 h-5 ${textSec}`} data-id="element-752" />
        </button>
        <button onClick={() => navigateTo('request_type')} className={`w-full p-5 rounded-2xl flex items-center justify-between text-left ${cardSurface}`} data-id="element-753">
          <div data-id="element-754">
            <p className={`text-[16px] font-bold ${textPri}`} data-id="element-755">Virtual Card</p>
            <p className={`text-[12px] mt-1 ${textSec}`} data-id="element-756">
              Instantly generate a virtual card
            </p>
          </div>
          <ChevronRightIcon className={`w-5 h-5 ${textSec}`} data-id="element-757" />
        </button>
      </div>
    </motion.div>;
  const renderRequestType = () => <motion.div key="request_type" custom={direction} variants={slideVariants} initial="enter" animate="center" exit="exit" className={`w-full min-h-screen pb-8 flex flex-col ${isDark ? 'bg-[#1A0E08]' : 'bg-page-warm'}`} data-id="element-758">
      <Header title="Select Tier" onBackClick={() => navigateTo('request_entry', -1)} data-id="element-759" />
      <div className="px-6 space-y-4 mt-4" data-id="element-760">
        <button onClick={() => navigateTo('request_delivery')} className={`w-full p-5 rounded-2xl flex items-center justify-between text-left border-2 border-[#F37024] ${isDark ? 'bg-[#3D2518]' : 'bg-[#FFF7F0]'}`} data-id="element-761">
          <div data-id="element-762">
            <p className={`text-[16px] font-bold ${textPri}`} data-id="element-763">Platinum</p>
            <p className={`text-[12px] mt-1 ${textSec}`} data-id="element-764">
              Higher limits, premium perks
            </p>
          </div>
          <CheckIcon className="w-5 h-5 text-[#F37024]" data-id="element-765" />
        </button>
        <button onClick={() => navigateTo('request_delivery')} className={`w-full p-5 rounded-2xl flex items-center justify-between text-left ${cardSurface}`} data-id="element-766">
          <div data-id="element-767">
            <p className={`text-[16px] font-bold ${textPri}`} data-id="element-768">Gold</p>
            <p className={`text-[12px] mt-1 ${textSec}`} data-id="element-769">
              Standard limits and features
            </p>
          </div>
        </button>
      </div>
    </motion.div>;
  const renderRequestDelivery = () => <motion.div key="request_delivery" custom={direction} variants={slideVariants} initial="enter" animate="center" exit="exit" className={`w-full min-h-screen pb-8 flex flex-col ${isDark ? 'bg-[#1A0E08]' : 'bg-page-warm'}`} data-id="element-770">
      <Header title="Delivery Details" onBackClick={() => navigateTo('request_type', -1)} data-id="element-771" />
      <div className="px-6 space-y-4 mt-4" data-id="element-772">
        <div className={`p-4 rounded-2xl ${cardSurface}`} data-id="element-773">
          <p className={`text-[12px] font-bold uppercase tracking-wider ${textSec} mb-2`} data-id="element-774">
            Delivery Address
          </p>
          <p className={`text-[14px] font-bold ${textPri}`} data-id="element-775">
            123 Main Street, Phase 5, DHA, Lahore
          </p>
        </div>
        <button onClick={() => navigateTo('request_review')} className="w-full py-4 mt-4 bg-[#5A1408] text-white font-bold text-[16px] rounded-2xl shadow-md active:scale-[0.98] transition-transform" data-id="element-776">
          Continue to Review
        </button>
      </div>
    </motion.div>;
  const renderRequestReview = () => <motion.div key="request_review" custom={direction} variants={slideVariants} initial="enter" animate="center" exit="exit" className={`w-full min-h-screen pb-8 flex flex-col ${isDark ? 'bg-[#1A0E08]' : 'bg-page-warm'}`} data-id="element-777">
      <Header title="Review Request" onBackClick={() => navigateTo('request_delivery', -1)} data-id="element-778" />
      <div className="px-6 space-y-4 mt-4" data-id="element-779">
        <div className={`p-5 rounded-2xl space-y-4 ${cardSurface}`} data-id="element-780">
          <div className="flex justify-between" data-id="element-781">
            <span className={`text-[13px] ${textSec}`} data-id="element-782">Card Type</span>
            <span className={`text-[13px] font-bold ${textPri}`} data-id="element-783">
              Platinum Debit
            </span>
          </div>
          <div className="flex justify-between" data-id="element-784">
            <span className={`text-[13px] ${textSec}`} data-id="element-785">Issuance Fee</span>
            <span className={`text-[13px] font-bold ${textPri}`} data-id="element-786">
              PKR 1,500
            </span>
          </div>
          <div className="flex justify-between" data-id="element-787">
            <span className={`text-[13px] ${textSec}`} data-id="element-788">Delivery Address</span>
            <span className={`text-[13px] font-bold text-right w-1/2 ${textPri}`} data-id="element-789">
              123 Main Street, Phase 5, DHA, Lahore
            </span>
          </div>
        </div>
        <button onClick={() => navigateTo('request_processing')} className="w-full py-4 mt-4 bg-[#5A1408] text-white font-bold text-[16px] rounded-2xl shadow-md active:scale-[0.98] transition-transform" data-id="element-790">
          Confirm Request
        </button>
      </div>
    </motion.div>;
  // Pay Bill Screens (reusing existing logic but condensed)
  const renderPayBill = () => {
    const numericAmount = Number(payAmount.replace(/[^0-9]/g, '')) || 0;
    const isValid = numericAmount >= CREDIT_CARD.minDue && numericAmount <= payFrom.balance;
    return <motion.div key="pay_bill" custom={direction} variants={slideVariants} initial="enter" animate="center" exit="exit" className={`w-full min-h-screen pb-8 ${isDark ? 'bg-[#1A0E08]' : 'bg-page-warm'}`} data-id="element-791">
        <Header title="Pay Bill" onBackClick={() => navigateTo('summary', -1)} data-id="element-792" />
        <div className="px-6 space-y-5" data-id="element-793">
          <div className={`rounded-2xl p-4 flex items-center justify-between ${cardSurface}`} data-id="element-794">
            <div data-id="element-795">
              <p className={`text-[11px] uppercase tracking-wider ${textSec}`} data-id="element-796">
                Due Date
              </p>
              <p className={`text-[15px] font-bold ${textPri} mt-0.5`} data-id="element-797">
                {CREDIT_CARD.dueDate}
              </p>
            </div>
            <div className="px-3 py-1.5 rounded-full text-[11px] font-bold text-[#C84B1100] bg-[#F3702400]" style={{
            background: 'rgba(243,112,36,0.12)'
          }} data-id="element-798">
              Total: PKR {formatPKR(CREDIT_CARD.totalDue)}
            </div>
          </div>
          <div data-id="element-799">
            <p className={`text-[12px] font-bold uppercase tracking-wider mb-2 ${isDark ? 'text-[#F37024]' : 'text-[#5A1408]'}`} data-id="element-800">
              Pay From
            </p>
            <button onClick={() => setShowAccountDropdown(!showAccountDropdown)} className={`w-full rounded-2xl p-4 flex items-center justify-between ${cardSurface}`} data-id="element-801">
              <div className="text-left" data-id="element-802">
                <p className={`text-[14px] font-bold ${textPri}`} data-id="element-803">
                  {payFrom.label}
                </p>
                <p className={`text-[12px] ${textSec}`} data-id="element-804">
                  {payFrom.number} · Bal: PKR {formatPKR(payFrom.balance)}
                </p>
              </div>
              <ChevronDownIcon className={`w-5 h-5 ${textSec}`} data-id="element-805" />
            </button>
            {showAccountDropdown && <div className={`mt-2 rounded-2xl overflow-hidden ${cardSurface}`} data-id="element-806">
                {PAY_FROM_ACCOUNTS.map(acc => <button key={acc.id} onClick={() => {
              setPayFrom(acc);
              setShowAccountDropdown(false);
            }} className={`w-full text-left p-4 flex items-center justify-between ${payFrom.id === acc.id ? isDark ? 'bg-[#3D2518]' : 'bg-[#FFF7F0]' : ''}`} data-id="element-807">
                    <div data-id="element-808">
                      <p className={`text-[14px] font-bold ${textPri}`} data-id="element-809">
                        {acc.label}
                      </p>
                      <p className={`text-[12px] ${textSec}`} data-id="element-810">
                        {acc.number} · PKR {formatPKR(acc.balance)}
                      </p>
                    </div>
                    {payFrom.id === acc.id && <CheckIcon className="w-5 h-5 text-[#F37024]" data-id="element-811" />}
                  </button>)}
              </div>}
          </div>
          <div data-id="element-812">
            <p className={`text-[12px] font-bold uppercase tracking-wider mb-2 ${isDark ? 'text-[#F37024]' : 'text-[#5A1408]'}`} data-id="element-813">
              Enter Amount
            </p>
            <div className={`rounded-2xl p-4 flex items-baseline gap-2 ${cardSurface}`} data-id="element-814">
              <span className={`text-[16px] font-bold ${textSec}`} data-id="element-815">PKR</span>
              <input type="tel" value={payAmount === '' ? '' : Number(payAmount).toLocaleString('en-US')} onChange={e => setPayAmount(e.target.value.replace(/[^0-9]/g, ''))} placeholder="0" className={`flex-1 outline-none bg-transparent text-[28px] font-bold ${textPri}`} data-id="element-816" />
            </div>
          </div>
          <button disabled={!isValid} onClick={() => navigateTo('review')} className="w-full py-4 bg-[#5A1408] text-white font-bold text-[16px] rounded-2xl shadow-md active:scale-[0.98] transition-transform disabled:opacity-40" data-id="element-817">
            Continue
          </button>
        </div>
      </motion.div>;
  };
  const renderReview = () => {
    const numericAmount = Number(payAmount.replace(/[^0-9]/g, '')) || 0;
    return <motion.div key="review" custom={direction} variants={slideVariants} initial="enter" animate="center" exit="exit" className={`w-full min-h-screen pb-8 flex flex-col ${isDark ? 'bg-[#1A0E08]' : 'bg-page-warm'}`} data-id="element-818">
        <Header title="Review" onBackClick={() => navigateTo('pay_bill', -1)} data-id="element-819" />
        <div className="px-5 flex-1 flex flex-col pb-6 space-y-4" data-id="element-820">
          <div className={`rounded-2xl px-5 py-5 text-center ${cardSurface}`} data-id="element-821">
            <div className="flex items-baseline justify-center gap-2" data-id="element-822">
              <span className="text-[16px] font-semibold text-[#F37024]" data-id="element-823">
                PKR
              </span>
              <span className={`text-[42px] font-bold leading-none ${textPri}`} data-id="element-824">
                {numericAmount.toLocaleString('en-US')}
              </span>
            </div>
          </div>
          <div className={`rounded-2xl px-5 py-5 ${cardSurface}`} data-id="element-825">
            <div className="flex gap-4 pb-4 border-b border-[rgba(0,0,0,0.05)]" data-id="element-826">
              <div className="flex-1 min-w-0" data-id="element-827">
                <p className="text-[14px] font-bold mb-1 text-[#C84B11]" data-id="element-828">
                  Pay From
                </p>
                <p className={`text-[13px] ${textPri}`} data-id="element-829">{payFrom.holder}</p>
                <p className={`text-[12px] ${textSec}`} data-id="element-830">{payFrom.label}</p>
              </div>
            </div>
            <div className="flex gap-4 pt-4" data-id="element-831">
              <div className="flex-1 min-w-0" data-id="element-832">
                <p className="text-[14px] font-bold mb-1 text-[#C84B11]" data-id="element-833">
                  Pay To
                </p>
                <p className={`text-[13px] ${textPri}`} data-id="element-834">Credit Card</p>
                <p className={`text-[12px] ${textSec}`} data-id="element-835">{CREDIT_CARD.number}</p>
              </div>
            </div>
          </div>
          <div className="flex-1" data-id="element-836" />
          <button onClick={() => navigateTo('processing')} className="w-full py-4 bg-[#5A1408] text-white font-bold text-[16px] rounded-2xl shadow-md active:scale-[0.98] transition-transform" data-id="element-837">
            Confirm Payment
          </button>
        </div>
      </motion.div>;
  };
  const renderProcessing = () => <motion.div key="processing" custom={direction} variants={slideVariants} initial="enter" animate="center" exit="exit" className={`w-full min-h-screen flex flex-col items-center justify-center ${isDark ? 'bg-[#1A0E08]' : 'bg-page-warm'}`} data-id="element-838">
      <motion.div animate={{
      rotate: 360
    }} transition={{
      duration: 1,
      repeat: Infinity,
      ease: 'linear'
    }} className="mb-6" data-id="element-839">
        <RefreshCwIcon className="w-14 h-14 text-[#F37024]" strokeWidth={1.5} data-id="element-840" />
      </motion.div>
      <p className={`text-[20px] font-bold mb-2 ${textPri}`} data-id="element-841">Processing...</p>
    </motion.div>;
  return <div className={`w-full min-h-screen relative overflow-hidden ${isDark ? 'bg-[#1A0E08]' : 'bg-page-warm'}`} data-id="element-842">
      <AnimatePresence mode="wait" custom={direction} data-id="element-843">
        {step === 'tabs' && renderTabs()}
        {step === 'summary' && renderSummary()}
        {step === 'manage_cards' && renderManageCards()}
        {step === 'card_detail' && renderCardDetail()}
        {step === 'card_controls' && renderCardControls()}
        {step === 'spending_limits' && renderSpendingLimits()}
        {step === 'blocked_merchants' && renderBlockedMerchants()}
        {step === 'pin_management' && renderPinManagement()}
        {step === 'pin_enter_new' && renderPinEnterNew()}
        {step === 'pin_confirm_new' && renderPinConfirmNew()}
        {step === 'pin_success' && <motion.div key="pin_success" custom={direction} variants={slideVariants} initial="enter" animate="center" exit="exit" className={`w-full min-h-screen pb-8 flex flex-col ${isDark ? 'bg-[#1A0E08]' : 'bg-page-warm'}`} data-id="element-844">
            <SuccessView title="PIN Changed" subtitle="Your card PIN has been successfully updated." actionText="Back to Card Details" onAction={() => navigateTo('card_detail', -1)} data-id="element-845" />
          </motion.div>}
        {step === 'activation_entry' && renderActivationEntry()}
        {step === 'activation_details' && renderActivationDetails()}
        {step === 'activation_set_pin' && renderActivationSetPin()}
        {step === 'activation_success' && <motion.div key="activation_success" custom={direction} variants={slideVariants} initial="enter" animate="center" exit="exit" className={`w-full min-h-screen pb-8 flex flex-col ${isDark ? 'bg-[#1A0E08]' : 'bg-page-warm'}`} data-id="element-846">
            <SuccessView title="Card Activated" subtitle="Your card is now ready to use." actionText="Back to Card Details" onAction={() => navigateTo('card_detail', -1)} data-id="element-847" />
          </motion.div>}
        {step === 'request_entry' && renderRequestEntry()}
        {step === 'request_type' && renderRequestType()}
        {step === 'request_delivery' && renderRequestDelivery()}
        {step === 'request_review' && renderRequestReview()}
        {step === 'request_processing' && renderProcessing()}
        {step === 'request_success' && <motion.div key="request_success" custom={direction} variants={slideVariants} initial="enter" animate="center" exit="exit" className={`w-full min-h-screen pb-8 flex flex-col ${isDark ? 'bg-[#1A0E08]' : 'bg-page-warm'}`} data-id="element-848">
            <SuccessView title="Request Submitted" subtitle="Your new card will arrive in 3-5 business days." actionText="Back to Cards" onAction={() => navigateTo('tabs', -1)} data-id="element-849" />
          </motion.div>}
        {step === 'block_entry' && renderBlockEntry()}
        {step === 'block_reason' && renderBlockReason()}
        {step === 'block_confirm' && renderBlockConfirm()}
        {step === 'block_success' && <motion.div key="block_success" custom={direction} variants={slideVariants} initial="enter" animate="center" exit="exit" className={`w-full min-h-screen pb-8 flex flex-col ${isDark ? 'bg-[#1A0E08]' : 'bg-page-warm'}`} data-id="element-850">
            <SuccessView title="Card Blocked" subtitle="Your card has been successfully blocked." actionText="Back to Cards" onAction={() => navigateTo('tabs', -1)} data-id="element-851" />
          </motion.div>}
        {step === 'toggle_confirm' && renderToggleConfirm()}
        {step === 'toggle_success' && <motion.div key="toggle_success" custom={direction} variants={slideVariants} initial="enter" animate="center" exit="exit" className={`w-full min-h-screen pb-8 flex flex-col ${isDark ? 'bg-[#1A0E08]' : 'bg-page-warm'}`} data-id="element-852">
            <SuccessView title="Settings Updated" subtitle="Your card controls have been updated." actionText="Back to Controls" onAction={() => navigateTo('card_controls', -1)} data-id="element-853" />
          </motion.div>}
        {step === 'pay_bill' && renderPayBill()}
        {step === 'review' && renderReview()}
        {step === 'processing' && renderProcessing()}
        {step === 'success' && <motion.div key="success" custom={direction} variants={slideVariants} initial="enter" animate="center" exit="exit" className={`w-full min-h-screen pb-8 flex flex-col ${isDark ? 'bg-[#1A0E08]' : 'bg-page-warm'}`} data-id="element-854">
            <SuccessView title="Payment Successful" subtitle="Your credit card bill has been paid." actionText="Back to Summary" onAction={() => navigateTo('summary', -1)} data-id="element-855" />
          </motion.div>}
        {step === 'apply_account' && renderApplyAccount()}
        {step === 'apply_scheme' && renderApplyScheme()}
        {step === 'apply_choose_card' && renderApplyChooseCard()}
        {step === 'apply_name_delivery' && renderApplyNameDelivery()}
        {step === 'apply_review' && renderApplyReview()}
        {step === 'apply_processing' && renderProcessing()}
        {step === 'apply_success' && <motion.div key="apply_success" custom={direction} variants={slideVariants} initial="enter" animate="center" exit="exit" className={`w-full min-h-screen pb-8 flex flex-col ${isDark ? 'bg-[#1A0E08]' : 'bg-page-warm'}`} data-id="element-856">
            <SuccessView title="Application Submitted" subtitle="Your new card will arrive in 3-5 business days." actionText="Back to Cards" onAction={() => navigateTo('tabs', -1)} data-id="element-857" />
          </motion.div>}
      </AnimatePresence>
    </div>;
}